/**
 * What a flag does, written for the admin page. The database description is
 * whatever someone typed when they created the row. This catalog is the
 * code's own account of each key, including keys the UI does not fetch.
 */
import { STAFF_TOOLS } from '@/lib/staff-tools/tools';

export const KNOWN_FLAGS_PAGE_SIZE = 10;

export type FlagEnforcedIn = 'ui' | 'api' | 'both';

export interface FlagCatalogEntry {
    key: string;
    description: string;
    enforcedIn: FlagEnforcedIn;
    /** QA dashboard keys. Hidden on the admin page in production. */
    qa?: boolean;
}

/**
 * Read by the API and not registered in FEATURE_FLAG_KEYS. Creating the row
 * here is how an admin turns them on. The UI does not gate on them.
 */
export const API_ONLY_FLAG_KEYS = [
    'credits-single-balance',
    'credits-packs-first',
    'studio-paid-credits-plan',
    'credit-survey-booking',
] as const;

export const FLAG_CATALOG: readonly FlagCatalogEntry[] = [
    {
        key: 'dashboards-native-visitor-trends',
        enforcedIn: 'ui',
        description:
            'Opens the Visitor Trends dashboard as its native Studio build instead of the Sigma embed. Off, missing or unreadable: Sigma, as before.',
    },
    {
        key: 'dashboards-native-app',
        enforcedIn: 'ui',
        description:
            'Opens the App Explorer dashboard as its native Studio build instead of the Sigma embed. Off, missing or unreadable: Sigma, as before.',
    },
    {
        key: 'dashboards-native-location',
        enforcedIn: 'ui',
        description:
            'Opens the Location Explorer dashboard as its native Studio build instead of the Sigma embed. Off, missing or unreadable: Sigma, as before.',
    },
    {
        key: 'dashboards-native-website',
        enforcedIn: 'ui',
        description:
            'Opens the Web Explorer dashboard as its native Studio build instead of the Sigma embed. Off, missing or unreadable: Sigma, as before.',
    },
    {
        key: 'dashboards-native-abc-norms',
        enforcedIn: 'ui',
        description:
            'Opens the ABC/DTS Norms dashboard as its native Studio build instead of the Sigma embed. Off, missing or unreadable: Sigma, as before.',
    },
    {
        key: 'dashboards-native-burlington-vtracker',
        enforcedIn: 'ui',
        description:
            'Opens the Burlington vTracker+ dashboard as its native Studio build instead of the Sigma embed. Off, missing or unreadable: Sigma, as before.',
    },
    {
        key: 'dashboards-native-cargurus-app-and-web-visitors-vtracker',
        enforcedIn: 'ui',
        description:
            'Opens the Cargurus vTracker+ dashboard as its native Studio build instead of the Sigma embed. Off, missing or unreadable: Sigma, as before.',
    },
    {
        key: 'dashboards-native-nacs-vtracker-plus',
        enforcedIn: 'ui',
        description:
            'Opens the NACS vTracker+ dashboard as its native Studio build instead of the Sigma embed. Off, missing or unreadable: Sigma, as before.',
    },
    {
        key: 'dashboards-native-iooh-brand-lift',
        enforcedIn: 'ui',
        description:
            'Opens the iOOH Brand Lift dashboard as its native Studio build instead of the Sigma embed. Off, missing or unreadable: Sigma, as before.',
    },
    {
        key: 'studio-ai-planner-mode',
        enforcedIn: 'both',
        description:
            'Turns on Ask DANI planner mode, report building in the chat, and the planner API. Off, those surfaces stay closed.',
    },
    {
        key: 'credit-consumption',
        enforcedIn: 'both',
        description:
            'Turns on the credits billing pages and the credits admin tools. Salesforce sync writes an org override on this key so a credits customer can open the dashboard.',
    },
    {
        key: 'studio-project-refresh',
        enforcedIn: 'ui',
        description:
            'Registered so the app asks about it on load. Nothing in the app reads this key, so turning it on changes nothing.',
    },
    {
        key: 'iooh-brand-lift',
        enforcedIn: 'ui',
        description:
            'Plan templates and banner templates tagged with this key stay hidden until the flag is on for the user.',
    },
    // reports-cross-shopping was retired in M4S-5699 when the DANI team took
    // ownership of the Cross Shopping row and de-flagged it (now flagless,
    // name-bound), and reports-llm-convo-signals in M4S-5700 when LLM Signals
    // went live on a flagless row. Both keys are gone from FEATURE_FLAG_KEYS, so
    // they are gone here too.
    {
        key: 'reports-ad-network-intelligence',
        enforcedIn: 'ui',
        description:
            'Scopes the Ad Network Intelligence report card to OOH networks whose media data we hold. The card is visible to everyone for now; once the card is set to require this flag, people without it do not see the card.',
    },
    {
        key: 'reports-llm-signals-coming-soon',
        enforcedIn: 'ui',
        description:
            'Holds the LLM Signals report back: on, its card on Home and in Board Reports reads Coming soon and cannot be built. Off, the report is live. Turn it on for everyone in an environment before the code reaches it, and off at launch.',
    },
    {
        key: 'crosstabs-dani-launch',
        enforcedIn: 'both',
        description:
            'Lets a DANI chat start a crosstab (the Build a crosstab card and button). Also needs DANI access, and is staff-only during the alpha whatever the flag says.',
    },
    {
        key: 'crosstabs-legacy-retired',
        enforcedIn: 'ui',
        description:
            'Retires the classic crosstab tool. Links and list rows open AI Crosstabs, and a saved classic crosstab opens as a conversion. The classic editor still answers its own URL.',
    },
    {
        key: 'studio-read-only',
        enforcedIn: 'ui',
        description: 'Opens surveys and Reel Studio shareout in a read-only mode for this user.',
    },
    {
        key: 'reel-studio-transcript-export',
        enforcedIn: 'ui',
        description: 'Shows the transcript download control in Reel Studio.',
    },
    {
        key: 'reel-studio-compare',
        enforcedIn: 'ui',
        description: 'Shows Compare in Reel Studio.',
    },
    {
        key: 'reel-studio-intro-slate',
        enforcedIn: 'ui',
        description:
            'Opens every Reel Studio MP4 on a branded title card with a DANI-drafted summary the user can edit. Turn it on only where the API render accepts the intro, or exports fail.',
    },
    {
        key: 'transcript-access-gate',
        enforcedIn: 'ui',
        description:
            'Turns on the Reel Studio transcript paywall. The API route enforces it. Keep it off for customers until the transcript backfill has run, or people who already have transcripts lose them.',
    },
    {
        key: 'studio-internal-nav',
        enforcedIn: 'ui',
        description:
            'Registered so the app asks about it on load. The nav no longer reads this key, so turning it on changes nothing.',
    },
    {
        key: 'studio-view-as',
        enforcedIn: 'both',
        description:
            'Staff View-as rollout. Grant it with a per-user override only. Turning it on for internal users, an org, or a group would turn View-as on for every staff member. The server re-reads it on every start, resume, check, and action.',
    },
    {
        key: 'studio-view-as-reports',
        enforcedIn: 'ui',
        description:
            'Lets a View-as session open Reports. Per-user overrides, and only after the planner forward is deployed in that environment.',
    },
    {
        key: 'studio-subsidiary',
        enforcedIn: 'ui',
        description:
            'Shows the client parent and subsidiary org switcher. It does not grant staff View-as.',
    },
    {
        key: 'spotlight-mining-of-insights',
        enforcedIn: 'ui',
        description:
            'Registered so the app asks about it on load. Nothing in the app reads this key, so turning it on changes nothing.',
    },
    {
        key: 'insight-engine-email-deep-links',
        enforcedIn: 'ui',
        description: 'Turns on the email links in Insight Engine.',
    },
    {
        key: 'feasibility-dani',
        enforcedIn: 'ui',
        description:
            'Opens the internal Feasibility with DANI tools, including rates and /ai/feasibility. Leave external users off. This is not the legacy studio.nav.feasibility tool.',
    },
    {
        key: 'feasibility-investigate',
        enforcedIn: 'both',
        description:
            'No longer read (2026-10-27): every staff member may start "Look into it further" on a feasibility check. Kept so existing per-user overrides are not lost; never turn it on for an org or for external users.',
    },
    {
        key: 'feasibility-client',
        enforcedIn: 'both',
        description:
            'Opens the client self-serve feasibility page at /dani-feasibility. The server audience check is what actually allows the request.',
    },
    {
        key: 'feasibility-handoff',
        enforcedIn: 'ui',
        description:
            'When a client feasibility check comes back Marginal or Not feasible, post it to Sales. Off, or unreadable, posts nothing. Clients never see the handoff.',
    },
    {
        key: 'oe-coding',
        enforcedIn: 'ui',
        description:
            'Reserved for the client rollout of AI open-end coding. Staff reach OE Coding today through the internal route list, so this flag does not gate them.',
    },
    {
        key: 'slide-creator-alpha',
        enforcedIn: 'both',
        description:
            'Internal alpha for Slide Creator. Per-user, and only together with being an internal user, so a client never gets in. Every slide-creation API route re-reads it.',
    },
    {
        key: 'hubspot-email-nurture',
        enforcedIn: 'ui',
        description:
            'Trial onboarding Enroll posts each user to HubSpot, which sends the four emails. Off, or unreadable, records a dry run and sends nothing.',
    },
    {
        key: 'survey-builder-native',
        enforcedIn: 'ui',
        description:
            "On: /builder opens Studio's own survey builder, and its API routes admit clients. Off, or unreadable: /builder shows the MFour Services builder as before.",
    },
    {
        key: 'dev-portal',
        enforcedIn: 'ui',
        description:
            "Shows the developer portal's staff page (/tools/dev-portal) and its Tools menu entry. The portal's API does not read it.",
    },
    {
        key: 'credits-observe-transcription',
        enforcedIn: 'both',
        description:
            'When on, transcription calls the credit observe charge path. The price stays on the API rate row. Off means that surface is not metered.',
    },
    {
        key: 'credits-observe-data-export',
        enforcedIn: 'both',
        description:
            'When on, data export calls the credit observe charge path. The price stays on the API rate row. Off means that surface is not metered.',
    },
    {
        key: 'credits-observe-crosstab-build',
        enforcedIn: 'both',
        description:
            'When on, a crosstab Run calls the credit observe charge path. The price stays on the API rate row. Off means that surface is not metered.',
    },
    {
        key: 'credits-observe-pptx-export',
        enforcedIn: 'both',
        description:
            'When on, a PowerPoint export calls the credit observe charge path. The price stays on the API rate row. Off means that surface is not metered.',
    },
    {
        key: 'credits-observe-video',
        enforcedIn: 'ui',
        description:
            'Registered so the app can read it. The API observe charge path does not meter video on this key yet, so turning it on does not start a charge.',
    },
    {
        key: 'credits-fleet-balance',
        enforcedIn: 'ui',
        description:
            'Staff only. Shows Tools → Credits → Organizations from the new credits balance, one table with a Source column, and adds DANI conversion changes to the Rate Card. Give it per-user overrides. Nothing a client sees changes.',
    },
    {
        key: 'studio.nav.feasibility',
        enforcedIn: 'ui',
        description:
            'Shows the legacy internal Feasibility tool in navigation. Feasibility with DANI uses feasibility-dani instead.',
    },
    {
        key: 'studio.nav.studio-catalog',
        enforcedIn: 'ui',
        description: 'Shows Studio Catalog in navigation and allows the /ai/studio-catalog route.',
    },
    {
        key: 'studio.nav.report-generation',
        enforcedIn: 'ui',
        description:
            'Legacy route guard for /slide-creation. Who may open Slide Creator is slide-creator-alpha.',
    },
    {
        key: 'studio.nav.abc-dts-norms',
        enforcedIn: 'ui',
        description: 'Shows ABC DTS norms in navigation and allows that route.',
    },
    {
        key: 'studio.nav.reports',
        enforcedIn: 'ui',
        description: 'Shows Reports in navigation and allows the /reports route.',
    },
    {
        key: 'studio.nav.spotlight',
        enforcedIn: 'ui',
        description: 'Shows Discover Spotlight in navigation and allows /discover/insights.',
    },
    {
        key: 'studio.nav.data-products',
        enforcedIn: 'ui',
        description:
            'Grandfather switch for Current Market Trends and Brand Image. Orgs that existed at rollout were granted it. New orgs are not, so those products stay hidden for them.',
    },
    {
        key: 'credits-single-balance',
        enforcedIn: 'api',
        description:
            'One annual credit pool for the billing org. Monthly caps on the billing card read as zero. Off unless an admin enables it for that org. The app does not read this key itself.',
    },
    {
        key: 'credits-packs-first',
        enforcedIn: 'api',
        description:
            'Draws credit packs before the contract for the billing org. Off means the contract is drawn first. The app does not read this key itself.',
    },
    {
        key: 'studio-paid-credits-plan',
        enforcedIn: 'api',
        description:
            'Treats the org as a paid credits plan when the API decides Studio access. Off or missing means the org is not on that plan. The app does not read this key itself.',
    },
    {
        key: 'credit-survey-booking',
        enforcedIn: 'api',
        description:
            'Shows the Survey Bookings card for the org. The API reads it together with planner mode. The app does not read this key itself.',
    },
    {
        key: 'qa.gate.demo-a',
        enforcedIn: 'ui',
        qa: true,
        description:
            'Demo gate on the feature-flag QA dashboard. It does not control a product feature.',
    },
    {
        key: 'qa.gate.demo-b',
        enforcedIn: 'ui',
        qa: true,
        description:
            'Demo gate on the feature-flag QA dashboard. It does not control a product feature.',
    },
    {
        key: 'qa.gate.demo-c',
        enforcedIn: 'ui',
        qa: true,
        description:
            'Demo gate on the feature-flag QA dashboard. It does not control a product feature.',
    },
    ...STAFF_TOOLS.map(
        (tool): FlagCatalogEntry => ({
            key: tool.flag,
            enforcedIn: 'both',
            description:
                ('description' in tool ? tool.description : undefined) ??
                `Opens the ${tool.label} staff tool${'href' in tool ? ` (${tool.href})` : ''} for this person, one of the staff tools for running surveys, panelists and fielding from Studio. Internal staff only; the server checks it again on every request, and off or unreadable keeps the tool closed.`,
        }),
    ),
];

const CATALOG_BY_KEY = new Map(FLAG_CATALOG.map((entry) => [entry.key, entry]));

export function catalogEntryFor(key: string): FlagCatalogEntry | undefined {
    return CATALOG_BY_KEY.get(key);
}

/** Saved description wins. A blank saved description falls back to the catalog and is not written back. */
export function resolvedFlagDescription(
    key: string,
    saved: string | null | undefined,
): { text: string; fromCatalog: boolean } {
    const trimmed = saved?.trim() ?? '';
    if (trimmed) return { text: trimmed, fromCatalog: false };
    const entry = catalogEntryFor(key);
    if (!entry) return { text: '', fromCatalog: false };
    return { text: entry.description, fromCatalog: true };
}

export function visibleFlagCatalog(includeQa: boolean): FlagCatalogEntry[] {
    return FLAG_CATALOG.filter((entry) => includeQa || !entry.qa)
        .slice()
        .sort((a, b) => a.key.localeCompare(b.key));
}

export function filterFlagCatalog(entries: FlagCatalogEntry[], query: string): FlagCatalogEntry[] {
    const needle = query.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter(
        (entry) =>
            entry.key.toLowerCase().includes(needle) ||
            entry.description.toLowerCase().includes(needle),
    );
}

export function pageFlagCatalog<T>(
    items: readonly T[],
    page: number,
    pageSize: number = KNOWN_FLAGS_PAGE_SIZE,
): {
    page: number;
    total: number;
    totalPages: number;
    items: T[];
    rangeStart: number;
    rangeEnd: number;
} {
    const total = items.length;
    const size = pageSize > 0 ? pageSize : 1;
    const totalPages = Math.max(1, Math.ceil(total / size));
    const safePage = Math.min(Math.max(1, page), totalPages);
    const start = (safePage - 1) * size;
    const slice = items.slice(start, start + size);
    return {
        page: safePage,
        total,
        totalPages,
        items: slice,
        rangeStart: total === 0 ? 0 : start + 1,
        rangeEnd: start + slice.length,
    };
}
